/**
 * t337u: checklists on a phone. t721u: `/checklists/templates/new` and `/checklists/templates/:id` open the shared
 * template builder (features/ops/ChecklistTemplateBuilder), the same page as on a computer. Settings › Checklist
 * templates lists them here. ops.checklist.template.manage to make or edit, ops.checklist.schedule.manage to schedule.
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { CHECKLIST_CATEGORIES, TEMPLATE_MANAGE_PERM, type ChecklistTemplate } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useCanAnywhere } from '@/lib/permissions';
import { Spinner } from '@/components/ui';
import { opsKeys } from '@/features/ops/common';
import { listCard, WsAddRow, WsRow } from './settings/WorkspaceSettings';
import { PHONE_NEW_BTN } from './headerNew';

export const templateNewPath = '/checklists/templates/new';
export const templatePath = (id: string) => `/checklists/templates/${id}`;
const kindName = (t: Pick<ChecklistTemplate, 'kind'>) => t.kind === 'register' ? 'Attendance register' : 'Checklist';
/** "Checklist · Opening · 6 items" / "Attendance register · Bangor". */
export const templateLine = (t: ChecklistTemplate) => t.kind === 'register'
  ? [kindName(t), t.register_location_name].filter(Boolean).join(' · ')
  : [t.is_audit ? 'Audit' : kindName(t), CHECKLIST_CATEGORIES.find(c => c.key === t.category)?.label, `${t.items.length} ${t.items.length === 1 ? 'item' : 'items'}`].filter(Boolean).join(' · ');

/** Settings › Checklist templates: the live templates (tap to edit) and New checklist. Archive / duplicate stay on a computer. */
export function ChecklistTemplatesPage() {
  const ws = useWorkspaceId();
  const nav = useNavigate();
  const canEdit = useCanAnywhere(TEMPLATE_MANAGE_PERM);
  const q = useQuery({ queryKey: opsKeys(ws ?? '', 'templates'), queryFn: () => api.ops.checklistTemplates(ws!, { archived: true }), enabled: Boolean(ws) });
  const list = (q.data ?? []).filter(t => !t.archived_at);
  return (
    <div className="min-h-0 flex-1 overflow-y-auto pb-4" data-testid="m-templates">
      <div className={`${listCard} mt-3`} data-testid="m-templates-list">
        {q.isLoading ? <div className="p-4"><Spinner /></div> : !list.length && <p className="border-b border-slate-100 px-3 py-3 text-sm text-slate-500 dark:border-slate-800">No checklists yet.</p>}
        {list.map(t => <WsRow key={t.id} testId="m-templates-row" title={t.name} line={templateLine(t)} onClick={canEdit ? () => nav(templatePath(t.id)) : undefined} />)}
        {canEdit && <WsAddRow label="New checklist" onClick={() => nav(templateNewPath)} testId="m-templates-add" />}
      </div>
    </div>
  );
}

/** The ＋ that opens a new checklist (Tasks › My checklists, All checklists); only for people who can make one. */
// `round` is kept for callers; t678u: every phone header + button is the same round size.
export function NewChecklistButton(_props: { round?: boolean } = {}) {
  const nav = useNavigate();
  if (!useCanAnywhere(TEMPLATE_MANAGE_PERM)) return null;
  return (
    <button type="button" onClick={() => nav(templateNewPath)} aria-label="New checklist" data-testid="m-checklists-new"
      className={PHONE_NEW_BTN}>
      <Plus className="h-5 w-5" strokeWidth={2.5} />
    </button>
  );
}
